import { CoachmarkContext } from './context';
import { CoachmarkProviderProps, CoachmarkTargetHighlightMode } from './types';
import { useCallback, useMemo, useRef, useState } from 'react';

const CoachmarkProvider = (props: CoachmarkProviderProps) => {
  const {
    children
  } = props;

  const targetsRef = useRef<Map<string, HTMLElement>>(new Map());
  const [activeTargetHighlightMode, setActiveTargetHighlightMode] = useState<CoachmarkTargetHighlightMode>('elevate');
  const [activeTargetId, setActiveTargetId] = useState<string | null>(null);
  const [isActiveTargetInteractive, setIsActiveTargetInteractive] = useState(false);
  const [targetVersion, setTargetVersion] = useState(0);

  const getTargetElement = useCallback((targetId: string) => {
    return targetsRef.current.get(targetId) ?? null;
  }, []);

  const registerTarget = useCallback((targetId: string, element: HTMLElement) => {
    targetsRef.current.set(targetId, element);
    setTargetVersion((version) => version + 1);
  }, []);

  const unregisterTarget = useCallback((targetId: string, element: HTMLElement) => {
    if (targetsRef.current.get(targetId) !== element) {
      return;
    }

    targetsRef.current.delete(targetId);
    setTargetVersion((version) => version + 1);
  }, []);

  const value = useMemo(() => {
    return {
      activeTargetHighlightMode,
      activeTargetId,
      getTargetElement,
      isActiveTargetInteractive,
      registerTarget,
      setActiveTargetHighlightMode,
      setActiveTargetId,
      setIsActiveTargetInteractive,
      targetVersion,
      unregisterTarget
    };
  }, [
    activeTargetHighlightMode,
    activeTargetId,
    getTargetElement,
    isActiveTargetInteractive,
    registerTarget,
    targetVersion,
    unregisterTarget
  ]);

  return <CoachmarkContext.Provider value={value}>{children}</CoachmarkContext.Provider>;
};

export default CoachmarkProvider;